import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Network, Plus, Pencil, Trash2, Users, Briefcase, Star, Bot, Shield } from 'lucide-react';
import RolePermissionsEditor from '../components/rbac/RolePermissionsEditor';
import SkillsManager from '../components/officers/SkillsManager';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function OrgChart() {
  const queryClient = useQueryClient();
  const [engineDialog, setEngineDialog] = useState(false);
  const [officerDialog, setOfficerDialog] = useState(false);
  const [assignDialog, setAssignDialog] = useState(false);
  const [editingEngine, setEditingEngine] = useState(null);
  const [editingOfficer, setEditingOfficer] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [assignmentForm, setAssignmentForm] = useState({
    owner_email: '',
    sponsor_email: ''
  });

  const [engineForm, setEngineForm] = useState({
    name: '',
    description: '',
    owner_email: '',
    status: 'active'
  });

  const [officerForm, setOfficerForm] = useState({
    user_email: '',
    callsign: '',
    rank_title: 'Cadet',
    clearance_level: 1,
    custom_role: 'Operator',
    skills: [],
    bot_owner_email: ''
  });

  const { data: engines, isLoading: enginesLoading } = useQuery({
    queryKey: ['engines'],
    queryFn: () => base44.entities.Engine.list(),
    initialData: []
  });

  const { data: officers, isLoading: officersLoading } = useQuery({
    queryKey: ['officers'],
    queryFn: () => base44.entities.OfficerProfile.list(),
    initialData: []
  });

  const { data: missions } = useQuery({
    queryKey: ['missions'],
    queryFn: () => base44.entities.Mission.list(),
    initialData: []
  });

  const { data: users } = useQuery({
    queryKey: ['users'],
    queryFn: () => base44.entities.User.list(),
    initialData: []
  });

  const { data: currentUser } = useQuery({
    queryKey: ['me'],
    queryFn: () => base44.auth.me()
  });

  const { data: currentProfile } = useQuery({
    queryKey: ['currentProfile', currentUser?.email],
    queryFn: async () => {
      const profiles = await base44.entities.OfficerProfile.list();
      return profiles.find((p) => p.user_email === currentUser?.email);
    },
    enabled: !!currentUser?.email
  });

  const isAdmin = currentUser?.role === 'admin';
  const isCommanderInChief = currentUser?.email === 'ari@reeftechnology.com';

  const createEngineMutation = useMutation({
    mutationFn: (data) => base44.entities.Engine.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engines'] });
      setEngineForm({ name: '', description: '', owner_email: '', status: 'active' });
      setEngineDialog(false);
      setEditingEngine(null);
    }
  });

  const updateEngineMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Engine.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engines'] });
      setEngineForm({ name: '', description: '', owner_email: '', status: 'active' });
      setEngineDialog(false);
      setEditingEngine(null);
    }
  });

  const deleteEngineMutation = useMutation({
    mutationFn: (id) => base44.entities.Engine.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['engines'] })
  });

  const createOfficerMutation = useMutation({
    mutationFn: (data) => {
      const isAIofficer = data.custom_role === 'AI Officer';
      const creatorIsCommander = isAdmin || currentProfile?.custom_role === 'Commander';
      
      // Automatically set is_bot=true for AI Officers
      const officerData = {
        ...data,
        is_bot: isAIofficer
      };
      
      // If AI Officer and creator is not a Commander, set pending approval
      if (isAIofficer && !creatorIsCommander) {
        officerData.bot_approval_status = 'pending';
        officerData.bot_approval_requested_by = currentUser?.email;
        officerData.bot_approval_requested_at = new Date().toISOString();
      } else if (isAIofficer && creatorIsCommander) {
        officerData.bot_approval_status = 'approved';
        officerData.bot_approved_by = currentUser?.email;
        officerData.bot_approved_at = new Date().toISOString();
      }
      
      return base44.entities.OfficerProfile.create(officerData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      setOfficerForm({ user_email: '', callsign: '', rank_title: 'Cadet', clearance_level: 1, custom_role: 'Operator', skills: [], bot_owner_email: '' });
      setOfficerDialog(false);
      setEditingOfficer(null);
    }
  });

  const updateOfficerMutation = useMutation({
    mutationFn: ({ id, data }) => {
      // Automatically set is_bot based on custom_role
      const officerData = {
        ...data,
        is_bot: data.custom_role === 'AI Officer'
      };
      return base44.entities.OfficerProfile.update(id, officerData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      setOfficerForm({ user_email: '', callsign: '', rank_title: 'Cadet', clearance_level: 1, custom_role: 'Operator', skills: [], bot_owner_email: '' });
      setOfficerDialog(false);
      setEditingOfficer(null);
    }
  });

  const deleteOfficerMutation = useMutation({
    mutationFn: (id) => base44.entities.OfficerProfile.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['officers'] })
  });

  const assignMissionMutation = useMutation({
    mutationFn: ({ missionId, data }) => {
      // Keep assigned_to_email and owner_email in sync
      const updateData = {
        ...data,
        assigned_to_email: data.owner_email
      };
      return base44.entities.Mission.update(missionId, updateData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['missions'] });
      setAssignDialog(false);
      setSelectedMission(null);
      setAssignmentForm({
        owner_email: '',
        sponsor_email: ''
      });
    }
  });

  const handleEngineSubmit = (e) => {
    e.preventDefault();
    if (editingEngine) {
      updateEngineMutation.mutate({ id: editingEngine.id, data: engineForm });
    } else {
      createEngineMutation.mutate(engineForm);
    }
  };

  const handleOfficerSubmit = (e) => {
    e.preventDefault();
    if (editingOfficer) {
      updateOfficerMutation.mutate({ id: editingOfficer.id, data: officerForm });
    } else {
      createOfficerMutation.mutate(officerForm);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail || !isAdmin) return;
    setInviting(true);
    try {
      await base44.users.inviteUser(inviteEmail, "user");
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      setInviteEmail('');
      alert(`Invitation sent to ${inviteEmail}`);
    } catch (error) {
      alert(`Failed to invite user: ${error.message}`);
    } finally {
      setInviting(false);
    }
  };

  const handleEditEngine = (engine) => {
    setEditingEngine(engine);
    setEngineForm({
      name: engine.name,
      description: engine.description || '',
      owner_email: engine.owner_email,
      status: engine.status
    });
    setEngineDialog(true);
  };

  const handleEditOfficer = (officer) => {
    setEditingOfficer(officer);
    setOfficerForm({
      user_email: officer.user_email,
      callsign: officer.callsign,
      rank_title: officer.rank_title,
      clearance_level: officer.clearance_level,
      custom_role: officer.custom_role || 'Operator',
      skills: officer.skills || [],
      bot_owner_email: officer.bot_owner_email || ''
    });
    setOfficerDialog(true);
  };

  const getMissionsByType = (type) => missions.filter((m) => m.game_mode === type);
  const getOfficerByEmail = (email) => officers.find((o) => o.user_email === email);
  const userExists = (email) => users.some((u) => u.email === email);

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Org Chart</h1>
          <p className="text-slate-400">Manage engines, officers, and mission assignments.</p>
        </div>
      </div>

      <Tabs defaultValue="engines" className="space-y-6">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="engines">Engines</TabsTrigger>
          <TabsTrigger value="officers">Officers</TabsTrigger>
          <TabsTrigger value="assignments">Mission Assignments</TabsTrigger>
          {isAdmin && <TabsTrigger value="access">User Access</TabsTrigger>}
          {isCommanderInChief && <TabsTrigger value="rbac">RBAC</TabsTrigger>}
        </TabsList>

        {/* ENGINES TAB */}
        <TabsContent value="engines" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={engineDialog} onOpenChange={(open) => {
              setEngineDialog(open);
              if (!open) {
                setEditingEngine(null);
                setEngineForm({ name: '', description: '', owner_email: '', status: 'active' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> New Engine
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="org-orgchart-dialog" className="bg-slate-900 border-slate-800 text-slate-100">
                <DialogHeader>
                  <DialogTitle>{editingEngine ? 'Edit Engine' : 'Create Engine'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleEngineSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Engine Name</Label>
                    <Input data-testid="org-orgchart-input"
                      value={engineForm.name}
                      onChange={(e) => setEngineForm({ ...engineForm, name: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      placeholder="e.g. Product Development"
                      required />

                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea data-testid="org-orgchart-textarea"
                      value={engineForm.description}
                      onChange={(e) => setEngineForm({ ...engineForm, description: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      placeholder="Purpose and scope..." />

                  </div>
                  <div className="space-y-2">
                    <Label>Owner Email</Label>
                    <Select
                      value={engineForm.owner_email}
                      onValueChange={(v) => setEngineForm({ ...engineForm, owner_email: v })}>

                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue placeholder="Select commander" />
                      </SelectTrigger>
                      <SelectContent>
                        {officers.filter(officer => officer.custom_role === 'Commander').map((officer) =>
                        <SelectItem key={officer.id} value={officer.user_email}>
                          {officer.callsign} - {officer.rank_title} ({officer.user_email})
                        </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={engineForm.status}
                      onValueChange={(v) => setEngineForm({ ...engineForm, status: v })}>

                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">
                    {editingEngine ? 'Update Engine' : 'Create Engine'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {engines.map((engine) =>
            <Card key={engine.id} className="glass-panel border-none">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-slate-700 text-lg font-semibold tracking-tight">{engine.name}</CardTitle>
                      <p className="text-sm text-slate-400 mt-1">{engine.description}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleEditEngine(engine)}
                      className="h-8 w-8 text-slate-500 hover:text-slate-300">

                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => deleteEngineMutation.mutate(engine.id)}
                      className="h-8 w-8 text-slate-500 hover:text-rose-400">

                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500">Owner</span>
                      <span className="text-slate-700">{engine.owner_email}</span>
                    </div>
                    <Badge variant={engine.status === 'active' ? 'default' : 'secondary'} className="uppercase text-xs">
                      {engine.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}
            {engines.length === 0 &&
            <div className="col-span-full py-12 text-center border border-dashed border-slate-800 rounded-xl">
                <Network className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-500">No engines created yet.</p>
              </div>
            }
          </div>
        </TabsContent>

        {/* OFFICERS TAB */}
        <TabsContent value="officers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={officerDialog} onOpenChange={(open) => {
              setOfficerDialog(open);
              if (!open) {
                setEditingOfficer(null);
                setOfficerForm({ user_email: '', callsign: '', rank_title: 'Cadet', clearance_level: 1, custom_role: 'Operator', skills: [], bot_owner_email: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700">
                  <Plus className="w-4 h-4 mr-2" /> New Officer
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="org-orgchart-dialog-1" className="bg-slate-900 border-slate-800 text-slate-100 max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingOfficer ? 'Edit Officer' : 'Add Officer'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleOfficerSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>User Email</Label>
                    <Input data-testid="org-orgchart-input-1"
                      type="email"
                      value={officerForm.user_email}
                      onChange={(e) => setOfficerForm({ ...officerForm, user_email: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      placeholder="officer@company.com"
                      disabled={!!editingOfficer}
                      required />

                    {!editingOfficer && officerForm.custom_role !== 'AI Officer' &&
                    <p className="text-xs text-slate-500">
                        You can create an officer profile before the user accepts the invitation
                      </p>
                    }
                    {!editingOfficer && officerForm.custom_role === 'AI Officer' &&
                    <p className="text-xs text-indigo-400">
                        🤖 AI Officers are activated immediately and don't require email invitations
                      </p>
                    }
                  </div>
                  <div className="space-y-2">
                    <Label>Callsign</Label>
                    <Input data-testid="org-orgchart-input-2"
                      value={officerForm.callsign}
                      onChange={(e) => setOfficerForm({ ...officerForm, callsign: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      placeholder="e.g. Maverick"
                      required />

                  </div>
                  <div className="space-y-2">
                    <Label>Rank</Label>
                    <Input data-testid="org-orgchart-input-3"
                      value={officerForm.rank_title}
                      onChange={(e) => setOfficerForm({ ...officerForm, rank_title: e.target.value })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      placeholder="e.g. Captain" />

                  </div>
                  <div className="space-y-2">
                    <Label>Clearance Level</Label>
                    <Input data-testid="org-orgchart-input-4"
                      type="number"
                      value={officerForm.clearance_level}
                      onChange={(e) => setOfficerForm({ ...officerForm, clearance_level: parseInt(e.target.value) })}
                      className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
                      min="1"
                      max="10" />

                  </div>
                  <div className="space-y-2">
                    <Label>Role (RBAC)</Label>
                    <Select
                      value={officerForm.custom_role}
                      onValueChange={(v) => setOfficerForm({ ...officerForm, custom_role: v })}>

                      <SelectTrigger className="bg-slate-950 border-slate-800">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {isCommanderInChief && (
                          <SelectItem value="Commander in Chief">👑 Commander in Chief - Supreme access</SelectItem>
                        )}
                        <SelectItem value="Commander">Commander - Full access to all features</SelectItem>
                        <SelectItem value="Senior Officer">Senior Officer - Officer access + Org Chart</SelectItem>
                        <SelectItem value="Officer">Officer - Manage missions and objectives</SelectItem>
                        <SelectItem value="Analyst">Analyst - View and analyze data</SelectItem>
                        <SelectItem value="Operator">Operator - Execute missions only</SelectItem>
                        <SelectItem value="AI Officer">🤖 AI Officer - AI Co-worker</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">Defines access permissions and available features</p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Skills & Proficiencies</Label>
                    <SkillsManager 
                      skills={officerForm.skills} 
                      onChange={(skills) => setOfficerForm({ ...officerForm, skills })}
                      readonly={!isAdmin && currentProfile?.custom_role !== 'Commander'}
                    />
                  </div>

                  {/* Bot Owner Selection - Only for AI Officers */}
                  {officerForm.custom_role === 'AI Officer' && (
                    <div className="space-y-2 p-4 bg-purple-900/20 rounded-lg border border-purple-500/30">
                      <Label className="text-purple-300">🤖 Bot Owner (Required)</Label>
                      <Select
                        value={officerForm.bot_owner_email}
                        onValueChange={(v) => setOfficerForm({ ...officerForm, bot_owner_email: v })}
                      >
                        <SelectTrigger className="bg-slate-950 border-slate-800">
                          <SelectValue placeholder="Select human owner for this bot" />
                        </SelectTrigger>
                        <SelectContent>
                          {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                            <SelectItem key={officer.id} value={officer.user_email}>
                              {officer.callsign} - {officer.rank_title} ({officer.custom_role})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-purple-400">
                        The bot owner is responsible for managing this AI co-worker.
                        {!(isAdmin || currentProfile?.custom_role === 'Commander') && (
                          <span className="block mt-1 text-amber-400">
                            ⚠️ Since you're not a Commander, this bot will require Commander approval.
                          </span>
                        )}
                      </p>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                    disabled={officerForm.custom_role === 'AI Officer' && !officerForm.bot_owner_email}
                  >
                    {editingOfficer ? 'Update Officer' : 
                      (officerForm.custom_role === 'AI Officer' && !(isAdmin || currentProfile?.custom_role === 'Commander')) 
                        ? 'Request Bot Approval' 
                        : 'Add Officer'}
                  </Button>
                </form>
                
                {!editingOfficer && isAdmin &&
                <div className="mt-4 pt-4 border-t border-slate-800">
                    <p className="text-sm text-slate-400 mb-3">Want to invite the user to the platform?</p>
                    <div className="flex gap-2">
                      <Input data-testid="org-orgchart-input-5"
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="email@example.com"
                      className="bg-slate-950 border-slate-800 flex-1" />

                      <Button
                      type="button"
                      onClick={handleInviteUser}
                      disabled={!inviteEmail || inviting}
                      className="bg-emerald-600 hover:bg-emerald-700">
                        {inviting ? 'Inviting...' : 'Invite to Platform'}
                      </Button>
                    </div>
                  </div>
                }
              </DialogContent>
            </Dialog>
          </div>

          <Card className="glass-panel border-none">
            <CardContent className="p-0">
              {officers.length === 0 ? (
                <div className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-500">No officers registered yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-800">
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Officer</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Email</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Rank</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Clearance</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Role</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">Skills</th>
                        <th className="text-left p-4 text-sm font-medium text-slate-500">XP</th>
                        <th className="text-right p-4 text-sm font-medium text-slate-500">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {officers.map((officer) => {
                        const officerUser = users.find((u) => u.email === officer.user_email);
                        const hasAcceptedInvite = !!officerUser;
                        const isBot = officer.is_bot || officer.custom_role === 'AI Officer';

                        return (
                          <tr key={officer.id} className="border-b border-slate-800 hover:bg-slate-900/30">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-slate-700 flex-shrink-0">
                                  {officer.avatar_url ? (
                                    <img src={officer.avatar_url} alt={officer.callsign} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className={`w-full h-full ${isBot ? 'bg-gradient-to-br from-purple-500 to-indigo-500' : 'bg-gradient-to-br from-indigo-500 to-cyan-500'} flex items-center justify-center`}>
                                      {isBot ? <Bot className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-slate-700 font-medium">
                                    {isBot && '🤖 '}{officer.callsign}
                                  </span>
                                  {!hasAcceptedInvite && !isBot && (
                                    <Badge variant="outline" className="text-amber-400 border-amber-400/30 text-xs">
                                      Pending
                                    </Badge>
                                  )}
                                  {isBot && (
                                    <>
                                      <Badge variant="outline" className="text-purple-400 border-purple-400/30 text-xs">
                                        AI Coworker
                                      </Badge>
                                      {officer.bot_approval_status === 'pending' && (
                                        <Badge variant="outline" className="text-amber-400 border-amber-400/30 text-xs">
                                          Pending Approval
                                        </Badge>
                                      )}
                                      {officer.bot_approval_status === 'rejected' && (
                                        <Badge variant="outline" className="text-red-400 border-red-400/30 text-xs">
                                          Rejected
                                        </Badge>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4">
                              <div>
                                <span className="text-sm text-slate-400">{officer.user_email}</span>
                                {isBot && officer.bot_owner_email && (
                                  <div className="text-xs text-purple-400 mt-0.5">
                                    Owner: {officers.find(o => o.user_email === officer.bot_owner_email)?.callsign || officer.bot_owner_email?.split('@')[0]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="p-4">
                              <span className="text-sm text-slate-600">{officer.rank_title}</span>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline" className="text-indigo-400 border-indigo-400/30 text-xs">
                                Level {officer.clearance_level}
                              </Badge>
                            </td>
                            <td className="p-4">
                              <Badge variant="outline" className={`
                                text-xs
                                ${officer.custom_role === 'Commander in Chief' ? 'text-amber-400 border-amber-400/30' : ''}
                                ${officer.custom_role === 'Commander' ? 'text-purple-400 border-purple-400/30' : ''}
                                ${officer.custom_role === 'Senior Officer' ? 'text-emerald-400 border-emerald-400/30' : ''}
                                ${officer.custom_role === 'Officer' ? 'text-blue-400 border-blue-400/30' : ''}
                                ${officer.custom_role === 'Analyst' ? 'text-cyan-400 border-cyan-400/30' : ''}
                                ${officer.custom_role === 'Operator' ? 'text-slate-400 border-slate-400/30' : ''}
                                ${officer.custom_role === 'AI Officer' ? 'text-indigo-400 border-indigo-400/30' : ''}
                              `}>
                                {officer.custom_role === 'Commander in Chief' && '👑 '}{officer.custom_role || 'Operator'}
                              </Badge>
                            </td>
                            <td className="p-4">
                              {officer.skills && officer.skills.length > 0 ? (
                                <div className="flex flex-wrap gap-1 max-w-xs">
                                  {officer.skills.slice(0, 3).map((skill, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/30 flex items-center gap-1">
                                      {skill.name}
                                      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                                      <span className="text-amber-400">{skill.level}</span>
                                    </Badge>
                                  ))}
                                  {officer.skills.length > 3 && (
                                    <Badge variant="outline" className="text-xs text-slate-500">
                                      +{officer.skills.length - 3}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-slate-600 text-sm">-</span>
                              )}
                            </td>
                            <td className="p-4">
                              {officer.total_xp !== undefined ? (
                                <span className="text-amber-400 font-mono text-sm">{officer.total_xp}</span>
                              ) : (
                                <span className="text-slate-600 text-sm">-</span>
                              )}
                            </td>
                            <td className="p-4">
                              <div className="flex gap-1 justify-end">
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => handleEditOfficer(officer)}
                                  className="h-8 w-8 text-slate-500 hover:text-slate-300"
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  onClick={() => deleteOfficerMutation.mutate(officer.id)}
                                  className="h-8 w-8 text-slate-500 hover:text-rose-400"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ASSIGNMENTS TAB */}
        <TabsContent value="assignments" className="space-y-6">
          <div className="space-y-6">
            {/* Rank & File */}
            <Card className="glass-panel border-none">
              <CardHeader>
                <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-slate-400" />
                  Rank & File Assignments
                </CardTitle>
                <p className="text-sm text-slate-400">Operations under engines</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getMissionsByType('rank_and_file').map((mission) => {
                    const ownerOfficer = getOfficerByEmail(mission.owner_email);
                    const sponsorOfficer = getOfficerByEmail(mission.sponsor_email);
                    return (
                      <div key={mission.id} className="bg-slate-100 text-slate-700 p-4 rounded-lg border border-slate-800">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-slate-600 font-medium">{mission.title}</h4>
                            <p className="text-sm text-slate-500">{mission.objective}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Owner:</span>
                              {ownerOfficer ? (
                                <Badge variant="outline" className="text-emerald-400 border-emerald-400/30">
                                  {ownerOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Sponsor:</span>
                              {sponsorOfficer ? (
                                <Badge variant="outline" className="text-purple-400 border-purple-400/30">
                                  {sponsorOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                          </div>
                          <Dialog open={assignDialog && selectedMission?.id === mission.id} onOpenChange={(open) => {
                            setAssignDialog(open);
                            if (!open) {
                              setSelectedMission(null);
                              setAssignmentForm({
                                owner_email: '',
                                sponsor_email: ''
                              });
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedMission(mission);
                                  setAssignmentForm({
                                    owner_email: mission.owner_email || '',
                                    sponsor_email: mission.sponsor_email || ''
                                  });
                                  setAssignDialog(true);
                                }}
                              >
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="org-orgchart-dialog-2" className="bg-slate-900 border-slate-800 text-slate-100">
                              <DialogHeader>
                                <DialogTitle>Manage Mission Assignments</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <p className="text-sm text-slate-400">Mission: {mission.title}</p>
                                
                                <div className="space-y-2">
                                  <Label>Owner</Label>
                                  <Select 
                                    value={assignmentForm.owner_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, owner_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select owner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label>Sponsor</Label>
                                  <Select 
                                    value={assignmentForm.sponsor_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, sponsor_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select sponsor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <Button 
                                  onClick={() => {
                                    assignMissionMutation.mutate({ 
                                      missionId: mission.id, 
                                      data: assignmentForm
                                    });
                                  }}
                                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                                >
                                  Update Assignments
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    );
                  })}
                  {getMissionsByType('rank_and_file').length === 0 &&
                  <p className="text-center py-8 text-slate-500">No rank & file missions</p>
                  }
                </div>
              </CardContent>
            </Card>

            {/* Campaigns */}
            <Card className="glass-panel border-none">
              <CardHeader>
                <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-400" />
                  Campaign Assignments
                </CardTitle>
                <p className="text-sm text-slate-400">Campaigns owned by Campaign Generals</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getMissionsByType('campaign').map((mission) => {
                    const ownerOfficer = getOfficerByEmail(mission.owner_email);
                    const sponsorOfficer = getOfficerByEmail(mission.sponsor_email);
                    return (
                      <div key={mission.id} className="bg-slate-100 p-4 rounded-lg border border-slate-800">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-slate-600 font-medium">{mission.title}</h4>
                            <p className="text-sm text-slate-500">{mission.objective}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Owner:</span>
                              {ownerOfficer ? (
                                <Badge variant="outline" className="text-indigo-400 border-indigo-400/30">
                                  {ownerOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Sponsor:</span>
                              {sponsorOfficer ? (
                                <Badge variant="outline" className="text-purple-400 border-purple-400/30">
                                  {sponsorOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                          </div>
                          <Dialog open={assignDialog && selectedMission?.id === mission.id} onOpenChange={(open) => {
                            setAssignDialog(open);
                            if (!open) {
                              setSelectedMission(null);
                              setAssignmentForm({
                                assigned_to_email: '',
                                owner_email: '',
                                sponsor_email: ''
                              });
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedMission(mission);
                                  setAssignmentForm({
                                    assigned_to_email: mission.assigned_to_email || '',
                                    owner_email: mission.owner_email || '',
                                    sponsor_email: mission.sponsor_email || ''
                                  });
                                  setAssignDialog(true);
                                }}
                              >
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="org-orgchart-dialog-3" className="bg-slate-900 border-slate-800 text-slate-100">
                              <DialogHeader>
                                <DialogTitle>Manage Mission Assignments</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <p className="text-sm text-slate-400">Campaign: {mission.title}</p>
                                
                                <div className="space-y-2">
                                  <Label>Assigned To (Execution)</Label>
                                  <Select 
                                    value={assignmentForm.assigned_to_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, assigned_to_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select officer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label>Owner</Label>
                                  <Select 
                                    value={assignmentForm.owner_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, owner_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select owner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label>Sponsor</Label>
                                  <Select 
                                    value={assignmentForm.sponsor_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, sponsor_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select sponsor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <Button 
                                  onClick={() => {
                                    assignMissionMutation.mutate({ 
                                      missionId: mission.id, 
                                      data: assignmentForm
                                    });
                                  }}
                                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                                >
                                  Update Assignments
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    );
                  })}
                  {getMissionsByType('campaign').length === 0 &&
                  <p className="text-center py-8 text-slate-500">No campaigns</p>
                  }
                </div>
              </CardContent>
            </Card>

            {/* Special Ops */}
            <Card className="glass-panel border-none">
              <CardHeader>
                <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-rose-400" />
                  Special Ops Assignments
                </CardTitle>
                <p className="text-sm text-slate-400">Critical operations for specialized officers</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {getMissionsByType('special_ops').map((mission) => {
                    const ownerOfficer = getOfficerByEmail(mission.owner_email);
                    const sponsorOfficer = getOfficerByEmail(mission.sponsor_email);
                    return (
                      <div key={mission.id} className="bg-slate-100 p-4 rounded-lg border border-slate-800">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-slate-600 font-medium">{mission.title}</h4>
                            <p className="text-sm text-slate-500">{mission.objective}</p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Owner:</span>
                              {ownerOfficer ? (
                                <Badge variant="outline" className="text-rose-400 border-rose-400/30">
                                  {ownerOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="text-slate-500">Sponsor:</span>
                              {sponsorOfficer ? (
                                <Badge variant="outline" className="text-purple-400 border-purple-400/30">
                                  {sponsorOfficer.callsign}
                                </Badge>
                              ) : (
                                <span className="text-slate-500">None</span>
                              )}
                            </div>
                          </div>
                          <Dialog open={assignDialog && selectedMission?.id === mission.id} onOpenChange={(open) => {
                            setAssignDialog(open);
                            if (!open) {
                              setSelectedMission(null);
                              setAssignmentForm({
                                assigned_to_email: '',
                                owner_email: '',
                                sponsor_email: ''
                              });
                            }
                          }}>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                onClick={() => {
                                  setSelectedMission(mission);
                                  setAssignmentForm({
                                    assigned_to_email: mission.assigned_to_email || '',
                                    owner_email: mission.owner_email || '',
                                    sponsor_email: mission.sponsor_email || ''
                                  });
                                  setAssignDialog(true);
                                }}
                              >
                                Manage
                              </Button>
                            </DialogTrigger>
                            <DialogContent data-testid="org-orgchart-dialog-4" className="bg-slate-900 border-slate-800 text-slate-100">
                              <DialogHeader>
                                <DialogTitle>Manage Mission Assignments</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4 mt-4">
                                <p className="text-sm text-slate-400">Operation: {mission.title}</p>
                                
                                <div className="space-y-2">
                                  <Label>Assigned To (Execution)</Label>
                                  <Select 
                                    value={assignmentForm.assigned_to_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, assigned_to_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select officer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label>Owner</Label>
                                  <Select 
                                    value={assignmentForm.owner_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, owner_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select owner" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <div className="space-y-2">
                                  <Label>Sponsor</Label>
                                  <Select 
                                    value={assignmentForm.sponsor_email} 
                                    onValueChange={(email) => setAssignmentForm({...assignmentForm, sponsor_email: email})}
                                  >
                                    <SelectTrigger className="bg-slate-950 border-slate-800">
                                      <SelectValue placeholder="Select sponsor" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value={null}>None</SelectItem>
                                      {officers.filter(o => !o.is_bot && o.custom_role !== 'AI Officer').map((officer) => (
                                        <SelectItem key={officer.id} value={officer.user_email}>
                                          {officer.callsign} ({officer.rank_title})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>

                                <Button 
                                  onClick={() => {
                                    assignMissionMutation.mutate({ 
                                      missionId: mission.id, 
                                      data: assignmentForm
                                    });
                                  }}
                                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                                >
                                  Update Assignments
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    );
                  })}
                  {getMissionsByType('special_ops').length === 0 &&
                  <p className="text-center py-8 text-slate-500">No special ops</p>
                  }
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* USER ACCESS TAB */}
        {isAdmin &&
        <TabsContent value="access" className="space-y-4">
            <Card className="glass-panel border-none">
              <CardHeader>
                <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Users className="w-5 h-5 text-indigo-400" />
                  User Access Management
                </CardTitle>
                <p className="text-sm text-slate-400">Invite and manage user access to the system</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Invite New User */}
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800">
                  <h4 className="text-slate-600 mb-3 font-semibold">Invite New User</h4>
                  <div className="flex gap-3">
                    <Input data-testid="org-orgchart-input-6"
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="Enter email address" className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 flex-1" />


                    <Button
                    onClick={handleInviteUser}
                    disabled={!inviteEmail || inviting}
                    className="bg-indigo-600 hover:bg-indigo-700">
                      <Plus className="w-4 h-4 mr-2" />
                      {inviting ? 'Inviting...' : 'Send Invitation'}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    New users will receive an email invitation to join the system
                  </p>
                </div>

                {/* Current Users */}
                <div>
                  <h4 className="text-slate-600 mb-3 font-semibold">Current Users</h4>
                  <div className="space-y-2">
                    {users.length === 0 ?
                  <div className="text-center py-8 border border-dashed border-slate-800 rounded-lg">
                        <p className="text-slate-500">No users in the system yet</p>
                      </div> :

                  <div className="space-y-2">
                        {users.map((user) => {
                      const hasOfficerProfile = officers.some((o) => o.user_email === user.email);
                      return (
                        <div key={user.id} className="bg-slate-100 p-3 rounded-lg flex items-center justify-between border border-slate-800">
                              <div className="flex-1">
                                <div className="flex items-center gap-3">
                                  <span className="text-slate-600 font-medium">{user.full_name}</span>
                                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                                    {user.role}
                                  </Badge>
                                  {hasOfficerProfile &&
                              <Badge variant="outline" className="text-emerald-400 border-emerald-400/30 text-xs">
                                      Officer Profile
                                    </Badge>
                              }
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <p className="text-sm text-slate-500">{user.email}</p>
                                  {hasOfficerProfile &&
                              <Badge variant="outline" className={`
                                      text-xs
                                      ${officers.find((o) => o.user_email === user.email)?.custom_role === 'Commander' ? 'text-purple-400 border-purple-400/30' : ''}
                                      ${officers.find((o) => o.user_email === user.email)?.custom_role === 'Officer' ? 'text-blue-400 border-blue-400/30' : ''}
                                      ${officers.find((o) => o.user_email === user.email)?.custom_role === 'Analyst' ? 'text-cyan-400 border-cyan-400/30' : ''}
                                      ${officers.find((o) => o.user_email === user.email)?.custom_role === 'Operator' ? 'text-slate-400 border-slate-400/30' : ''}
                                    `}>
                                      {officers.find((o) => o.user_email === user.email)?.custom_role || 'Operator'}
                                    </Badge>
                              }
                                </div>
                              </div>
                              {!hasOfficerProfile &&
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setOfficerForm({
                                user_email: user.email,
                                callsign: user.full_name.split(' ')[0] || '',
                                rank_title: 'Cadet',
                                clearance_level: 1,
                                custom_role: 'Operator',
                                skills: []
                              });
                              setOfficerDialog(true);
                            }}
                            className="text-indigo-400 border-indigo-400/30 hover:bg-indigo-500/10">
                                  Create Officer Profile
                                </Button>
                          }
                            </div>);

                    })}
                      </div>
                  }
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        }

        {/* RBAC TAB - Commander in Chief Only */}
        {isCommanderInChief && (
          <TabsContent value="rbac" className="space-y-4">
            <Card className="glass-panel border-none">
              <CardHeader>
                <CardTitle className="text-slate-700 font-semibold tracking-tight leading-none flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-400" />
                  Role-Based Access Control (RBAC)
                </CardTitle>
                <p className="text-sm text-slate-400">Manage role permissions and access levels. Only the Commander in Chief can modify these settings.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Role Hierarchy with Editable Permissions */}
                <RolePermissionsEditor 
                  officers={officers} 
                  currentUserEmail={currentUser?.email}
                />

                {/* Officers by Role */}
                <div className="bg-slate-100 p-4 rounded-lg border border-slate-800">
                  <h4 className="text-slate-600 mb-4 font-semibold">Officers by Role</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-300">
                          <th className="text-left p-2 text-sm font-medium text-slate-500">Officer</th>
                          <th className="text-left p-2 text-sm font-medium text-slate-500">Current Role</th>
                          <th className="text-left p-2 text-sm font-medium text-slate-500">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {officers.filter(o => !o.is_bot).map((officer) => (
                          <tr key={officer.id} className="border-b border-slate-200">
                            <td className="p-2">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-700 font-medium">{officer.callsign}</span>
                                <span className="text-xs text-slate-500">({officer.user_email})</span>
                              </div>
                            </td>
                            <td className="p-2">
                              <Badge variant="outline" className={`
                                text-xs
                                ${officer.custom_role === 'Commander in Chief' ? 'text-amber-400 border-amber-400/30' : ''}
                                ${officer.custom_role === 'Commander' ? 'text-purple-400 border-purple-400/30' : ''}
                                ${officer.custom_role === 'Senior Officer' ? 'text-emerald-400 border-emerald-400/30' : ''}
                                ${officer.custom_role === 'Officer' ? 'text-blue-400 border-blue-400/30' : ''}
                                ${officer.custom_role === 'Analyst' ? 'text-cyan-400 border-cyan-400/30' : ''}
                                ${officer.custom_role === 'Operator' ? 'text-slate-400 border-slate-400/30' : ''}
                              `}>
                                {officer.custom_role === 'Commander in Chief' && '👑 '}{officer.custom_role || 'Operator'}
                              </Badge>
                            </td>
                            <td className="p-2">
                              <Select
                                value={officer.custom_role || 'Operator'}
                                onValueChange={(newRole) => {
                                  updateOfficerMutation.mutate({ 
                                    id: officer.id, 
                                    data: { ...officer, custom_role: newRole }
                                  });
                                }}
                              >
                                <SelectTrigger className="h-8 w-40 bg-white border-slate-300 text-slate-900">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Commander in Chief">👑 Commander in Chief</SelectItem>
                                  <SelectItem value="Commander">Commander</SelectItem>
                                  <SelectItem value="Senior Officer">Senior Officer</SelectItem>
                                  <SelectItem value="Officer">Officer</SelectItem>
                                  <SelectItem value="Analyst">Analyst</SelectItem>
                                  <SelectItem value="Operator">Operator</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>);

}